import { useCallback, useEffect, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  ActionIcon,
  Anchor,
  Breadcrumbs,
  Indicator,
  Tooltip,
} from '@mantine/core'
import { HelpCircle, Bell } from 'lucide-react'

import { useLocation, navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { ProfileMenu } from 'src/components/Navigation/ProfileMenu/ProfileMenu'
import { GET_APPLICANT_NAME_BY_ID } from 'src/graphql/applicants.graphql'
import { GET_NOTIFICATIONS } from 'src/graphql/notifications.graphql'
import { GET_VOE_DRIVER_NAME_BY_ID } from 'src/graphql/voe.graphql'
import IconMenu from 'src/icons/IconMenu'
import { buildFullName } from 'src/lib/formatters'
import { useWebsockets } from 'src/pages/ApplicantsPage/context/WebSocketsContext'

import GlobalSearchBar from './GlobalSearchBar'

const BREADCRUMB_LABELS: Record<string, string> = {
  'voe-requests': 'VoE Requests',
}

const formatBreadcrumbLabel = (segment: string) =>
  BREADCRUMB_LABELS[segment] ?? segment.replaceAll('-', ' ')

interface HeaderProps {
  toggleNotifications?: any
  showSidebarOnMobile?: boolean
  setShowSidebarOnMobile?: (show: boolean) => void
  openSearch?: () => void
  showSearch?: boolean
  showNotifications?: boolean
  title?: string
}

const Header: React.FC<HeaderProps> = ({
  toggleNotifications,
  showSidebarOnMobile = false,
  setShowSidebarOnMobile,
  openSearch,
  showSearch = true,
  showNotifications = true,
  title,
}) => {
  const [section, setSection] = useState(null)
  const websocketsData = useWebsockets()
  const { pathname } = useLocation()

  // Query unread notifications to show indicator
  const { data: notificationsData, refetch: refetchNotifications } = useQuery(
    GET_NOTIFICATIONS,
    {
      skip: !showNotifications,
      variables: {
        pageInfo: { offset: 0, limit: 1 },
        filters: { types: [] },
      },
    }
  )

  const unreadNotificationsCount =
    notificationsData?.notifications?.totalCount ?? 0
  const hasUnreadNotifications = unreadNotificationsCount > 0

  // Show indicator if there are unread notifications OR a new one just came through websocket
  const showNotificationIndicator =
    hasUnreadNotifications || !!websocketsData.newNotification

  // Refetch notifications when new notification comes through websocket
  useEffect(() => {
    if (showNotifications && websocketsData.newNotification) {
      refetchNotifications()
    }
  }, [showNotifications, websocketsData.newNotification, refetchNotifications])

  const onNotificationClick = () => {
    toggleNotifications.toggle()
    // Refetch notifications to update the indicator when drawer opens/closes
    refetchNotifications()
  }

  const handleHelpClick = useCallback(() => {
    if (window.showIntercom) {
      window.showIntercom()
    }
  }, [])

  const firstBreadcrumb = pathname.split('/')[1]
  const secondBreadcrumb =
    pathname.length >= 3 &&
    (firstBreadcrumb === 'applicants' || firstBreadcrumb === 'voe-requests')
      ? pathname.split('/')[2]
      : null

  const [loadApplicant] = useLazyQuery(GET_APPLICANT_NAME_BY_ID, {
    onCompleted: (data) => {
      const fullName = buildFullName({
        firstName: data.applicant.firstName,
        middleName: data.applicant.middleName,
        lastName: data.applicant.lastName,
      })
      setSection(fullName)
    },
    onError: (error) => {
      console.error('Error fetching applicant: ', error.message)
    },
  })

  const [loadVoE] = useLazyQuery(GET_VOE_DRIVER_NAME_BY_ID, {
    onCompleted: (data) => {
      setSection(data.verificationOfEmployment.driverName)
    },
    onError: () => {
      console.log('error', 'Unable to fetch VoE')
    },
  })

  useEffect(() => {
    if (secondBreadcrumb) {
      if (firstBreadcrumb === 'applicants') {
        loadApplicant({ variables: { id: secondBreadcrumb } })
      } else if (firstBreadcrumb === 'voe-requests') {
        loadVoE({ variables: { id: secondBreadcrumb } })
      }
    }
  }, [secondBreadcrumb])

  return (
    <div className="flex h-[7%] w-full flex-row items-center justify-between rounded-lg border-b bg-white px-6 py-4">
      <ActionIcon
        variant="subtle"
        className="sm:hidden"
        onClick={() => setShowSidebarOnMobile(!showSidebarOnMobile)}
      >
        <IconMenu
          className={`fill-none stroke-doubleNickel-gray-700 active:stroke-doubleNickel-gray-900`}
        />
      </ActionIcon>
      {title ? (
        <div className="rounded-lg p-2 text-sm font-medium text-doubleNickel-gray-700">
          {title}
        </div>
      ) : (
        <Breadcrumbs>
          <Anchor
            onClick={() => navigate(`/${firstBreadcrumb}`)}
            className={`rounded-lg p-2 text-sm font-medium text-doubleNickel-gray-700 ${
              BREADCRUMB_LABELS[firstBreadcrumb] ? '' : 'capitalize'
            }`}
          >
            {formatBreadcrumbLabel(firstBreadcrumb)}
          </Anchor>
          {secondBreadcrumb && (
            <Anchor
              onClick={() =>
                navigate(`/${firstBreadcrumb}/${secondBreadcrumb}`)
              }
              className="rounded-lg p-2 text-sm font-medium capitalize text-doubleNickel-gray-700 "
            >
              {section}
            </Anchor>
          )}
        </Breadcrumbs>
      )}
      <div className="ml-auto flex flex-row items-center gap-6">
        {showSearch && <GlobalSearchBar handleClick={openSearch} />}
        {showNotifications && (
          <Tooltip
            key={'Notifications'}
            label={'Notifications'}
            position="left"
          >
            <ActionIcon
              variant="subtle"
              aria-label="Notifications"
              onClick={onNotificationClick}
            >
              <Indicator
                size={6}
                color={showNotificationIndicator ? '#1C93D7' : 'transparent'}
              >
                <Bell size={18} className="stroke-doubleNickel-gray-400" />
              </Indicator>
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip
          key={'Help & Support'}
          label={'Help & Support'}
          position="left"
        >
          <ActionIcon
            variant="subtle"
            onClick={handleHelpClick}
            className="mt-0.5"
          >
            <HelpCircle size={18} className="stroke-doubleNickel-gray-400" />
          </ActionIcon>
        </Tooltip>

        <ProfileMenu />
      </div>
    </div>
  )
}

export default Header
